'use client'
import * as React from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import { gsap } from 'gsap'
import { useGSAP } from '@gsap/react'

import { cn } from '../../lib/utils'

type TabsSize = 'sm' | 'md' | 'lg'
type TabsColor = 'neutral' | 'base'

const sizeTriggerStyles: Record<TabsSize, string> = {
  sm: 'pt-2xs pb-3xs px-2xs text-xs',
  md: 'pt-xs pb-2xs px-xs text-sm',
  lg: 'pt-sm pb-2xs px-xs text-md',
}

const colorTriggerStyles: Record<TabsColor, string> = {
  base: 'text-neutral-surface-80 data-[state=active]:text-base-surface-100 data-[state=active]:font-bold',
  neutral:
    'text-neutral-surface-80 data-[state=active]:text-neutral-surface-100 data-[state=active]:font-bold',
}

const indicatorColorStyles: Record<TabsColor, string> = {
  base: 'bg-base-main-light',
  neutral: 'bg-neutral-main-light',
}

interface TabsContextProps {
  size: TabsSize
  color: TabsColor
  scrollable?: boolean
  value?: string
  onValueChange?: (value: string) => void
}

const TabsContext = React.createContext<TabsContextProps | undefined>(undefined)

interface TabsProps extends React.ComponentProps<typeof TabsPrimitive.Root> {
  size?: TabsSize
  color?: TabsColor
  scrollable?: boolean
}

function Tabs({
  className,
  size = 'md',
  color = 'neutral',
  scrollable = false,
  value: valueProp,
  onValueChange: onValueChangeProp,
  ...props
}: TabsProps) {
  // Controlled/uncontrolled logic
  const [internalValue, setInternalValue] = React.useState<string | undefined>(
    undefined,
  )
  const isControlled =
    valueProp !== undefined && onValueChangeProp !== undefined
  const value = isControlled ? valueProp : internalValue
  const onValueChange = isControlled ? onValueChangeProp : setInternalValue

  return (
    <TabsContext.Provider
      value={{ size, color, scrollable, value, onValueChange }}
    >
      <TabsPrimitive.Root
        data-slot='tabs'
        className={cn('flex flex-col gap-2 overflow-hidden ', className)}
        value={value}
        onValueChange={onValueChange}
        {...props}
      />
    </TabsContext.Provider>
  )
}

function TabsList({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) {
  const ctx = React.useContext(TabsContext)

  const color = ctx?.color ?? 'neutral'
  const listRef = React.useRef<HTMLDivElement>(null)
  const indicatorRef = React.useRef<HTMLDivElement>(null)
  const [activeTab, setActiveTab] = React.useState<HTMLElement | null>(null)
  const [scrollLeft, setScrollLeft] = React.useState(0)
  const isLoaded = React.useRef(false)

  // Track scroll position
  React.useEffect(() => {
    const listElement = listRef.current
    if (!listElement) return
    const handleScroll = () => {
      setScrollLeft(listElement.scrollLeft || 0)
    }
    listElement.addEventListener('scroll', handleScroll, { passive: true })
    setTimeout(() => {
      isLoaded.current = true
    }, 100)
    return () => {
      listElement.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // Detect active tab changes using MutationObserver (works for controlled/uncontrolled)
  React.useEffect(() => {
    if (!listRef.current) return
    const updateActive = () => {
      const triggers = Array.from(
        listRef.current!.querySelectorAll('[data-slot="tabs-trigger"]'),
      )
      const active = triggers.find(
        (el) => el.getAttribute('data-state') === 'active',
      ) as HTMLElement | undefined
      setActiveTab(active || null)
    }
    updateActive()
    const observer = new MutationObserver(updateActive)
    observer.observe(listRef.current, {
      subtree: true,
      attributes: true,
      attributeFilter: ['data-state'],
    })
    return () => observer.disconnect()
  }, [])

  // Animate indicator
  useGSAP(() => {
    if (!indicatorRef.current || !activeTab || !listRef.current) return

    // Kill any ongoing animations to prevent conflicts
    gsap.killTweensOf(indicatorRef.current)

    // Use requestAnimationFrame to ensure DOM is updated before reading dimensions
    requestAnimationFrame(() => {
      if (!indicatorRef.current || !activeTab || !listRef.current) return

      // Get position relative to parent container (accounting for scroll)
      const parentContainer = indicatorRef.current.parentElement
      if (!parentContainer) return

      const tabRect = activeTab.getBoundingClientRect()
      const containerRect = parentContainer.getBoundingClientRect()

      // Calculate position relative to parent container
      const x = tabRect.right - containerRect.right
      const width = tabRect.width

      gsap.to(indicatorRef.current, {
        x,
        width,
        duration: isLoaded.current ? 0.35 : 0,
        ease: 'power2.out',
        overwrite: true, // Automatically kill conflicting animations
      })
    })
  }, [activeTab, scrollLeft, isLoaded.current])

  // Scroll by a fixed amount (design token spacing)
  // const scrollAmount = 120

  // const handleScrollLeft = () => {
  //   if (listRef.current) {
  //     listRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' })
  //   }
  // }
  // const handleScrollRight = () => {
  //   if (listRef.current) {
  //     listRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' })
  //   }
  // }

  // Dynamic indicator color class
  const indicatorColorClass =
    indicatorColorStyles[color] || 'bg-neutral-main-light'

  return (
    <div className={cn('relative flex items-center w-full')}>
      {/* Only one indicator, outside the TabsPrimitive.List */}
      <div
        ref={indicatorRef}
        className={cn(
          'absolute bottom-0 righ-0 h-3xs transition-none pointer-events-none rounded-3xs',
          indicatorColorClass,
        )}
        style={{ willChange: 'transform, width' }}
      />
      <TabsPrimitive.List
        ref={listRef}
        data-slot='tabs-list'
        className={cn(
          'inline-flex justify-start items-center w-full overflow-x-auto   scroll-smooth mobile-scrollbar touch-pan-x scrollbar-thin scrollbar-thumb-muted/4 scrollbar-track-transparent',
          className,
        )}
        {...props}
      />
    </div>
  )
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const ctx = React.useContext(TabsContext)
  const size = ctx?.size ?? 'md'
  const color = ctx?.color ?? 'neutral'
  const scrollable = ctx?.scrollable

  // Improved drag/click logic for scrollable tabs (desktop & mobile)
  const isDragging = React.useRef(false)
  const dragStartX = React.useRef(0)
  const pointerDown = React.useRef(false)

  function handlePointerDown(e: React.PointerEvent) {
    if (!scrollable) return
    pointerDown.current = true
    isDragging.current = false
    dragStartX.current = e.clientX
  }

  function handlePointerMove(e: React.PointerEvent) {
    if (!scrollable || !pointerDown.current) return
    if (Math.abs(e.clientX - dragStartX.current) > 5) {
      isDragging.current = true
    }
  }

  function handlePointerUp(e: React.PointerEvent) {
    if (!scrollable) return
    pointerDown.current = false
    // If it was a drag, prevent tab selection
    if (isDragging.current) {
      e.preventDefault()
      e.stopPropagation()
    }
    // Reset drag state
    setTimeout(() => {
      isDragging.current = false
    }, 0)
  }

  function handleClick(e: React.MouseEvent) {
    if (!scrollable) return
    if (isDragging.current) {
      e.preventDefault()
      e.stopPropagation()
      isDragging.current = false
    }
  }

  return (
    <TabsPrimitive.Trigger
      data-slot='tabs-trigger'
      className={cn(
        'cursor-pointer focus-visible:outline-ring inline-flex flex-1 items-center justify-center border-neutral-surface-dark font-regular border-b-2  whitespace-nowrap focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50',
        sizeTriggerStyles[size],
        colorTriggerStyles[color],
        scrollable && 'flex-shrink-0',
        className,
      )}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      onClick={handleClick}
      {...props}
    />
  )
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  const contentRef = React.useRef<HTMLDivElement>(null)
  // const ctx = React.useContext(TabsContext)
  // Animate content in/out on every tab change
  // useGSAP(() => {
  //   if (!contentRef.current) return
  //   gsap.fromTo(
  //     contentRef.current,
  //     { opacity: 0, y: 16 },
  //     { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out' },
  //   )
  // }, [ctx?.value, props.value])
  return (
    <TabsPrimitive.Content
      ref={contentRef}
      data-slot='tabs-content'
      className={cn('flex-1 outline-none', className)}
      key={props.value}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent }
